
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { VanityAPI } from '../services/api';
import { VanityPage } from '../types';
import { unescapeHtml } from '../utils/unescapeHtml';
import { sanitizeLimitedHtml } from '../utils/sanitizeLimitedHtml';
import TemplateContact from '../components/TemplateContact';
import TemplateArchive from '../components/TemplateArchive';
import SEOPage from '../components/SEOPage';
import seoGlobals from '../seo_globals.json';

const DynamicPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [page, setPage] = useState<VanityPage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadPage = async () => {
            if (!slug) return;
            setLoading(true);
            try {
                const data = await VanityAPI.getPublicPage(slug);
                setPage(data);
                document.title = `${data.title} | VANITY`;
            } catch (err: any) {
                console.error("Failed to load page:", err);
                setError(err.message || 'Page not found');
            } finally {
                setLoading(false);
            }
        };
        loadPage();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-vanity-white">
                <div className="w-12 h-12 border-4 border-vanity-pink border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error || !page) {
        return (
            <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-4">
                <h1 className="font-serif text-6xl mb-8">Not Found.</h1>
                <p className="text-gray-400 uppercase tracking-widest font-black text-sm">The requested anthology node is unavailable.</p>
                <a href="/" className="mt-12 bg-black text-white px-10 py-4 rounded-full text-xs font-black uppercase tracking-widest">Return Home</a>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn">
            <SEOPage
                title={page.seoTitle || `${page.title} | VANITY`}
                description={page.seoDescription || seoGlobals.seo.defaultDescription}
            />
            {/* Hero Section */}
            {page.heroImageUrl && (
                <div className="relative h-[60vh] md:h-[70vh] overflow-hidden">
                    <img
                        src={page.heroImageUrl}
                        alt={page.featuredImageAltText || page.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center px-4 max-w-4xl">
                            <h1 className="font-serif text-vanity-white text-5xl md:text-8xl leading-none">
                                {page.title}
                            </h1>
                        </div>
                    </div>
                </div>
            )}

            {/* Content Section */}
            <div className={`mx-auto px-6 py-20 md:py-12 ${!page.heroImageUrl ? 'mt-20' : ''}`}>
                {!page.heroImageUrl && page.slug !== 'contact' && page.slug !== 'archive' && (
                    <h1 className="font-serif text-5xl md:text-6xl text-vanity-black mb-10 text-center max-w-4xl mx-auto">
                        {page.title}
                    </h1>
                )}

                {/* Page Narrative (Intro / Content) - Always Visible */}
                {page.content && page.slug !== 'contact' && page.slug !== 'archive' && (
                    <div className="max-w-4xl mx-auto mb-20 md:mb-15">
                        <div className="font-sans text-vanity-black/70 text-lg leading-relaxed text-center">
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeLimitedHtml(unescapeHtml(page.content))
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Modular Content - Only if not a templated page */}
                {page.contentModules && page.contentModules.length > 0 && page.slug !== 'contact' && page.slug !== 'archive' && (
                    <div className="space-y-20 md:space-y-10 animate-fadeIn">
                        {page.contentModules.map((m, idx) => (
                            <section key={m.id} className="max-w-[1440px] mx-auto px-8 md:px-12 py-5">
                                {m.type === 'text' ? (
                                    <div className="max-w-4xl mx-auto text-center space-y-8">
                                        {m.title && <h2 className="font-serif text-4xl md:text-5xl leading-tight">{m.title}</h2>}
                                        {m.text && (
                                            <div className="vanity-content font-sans text-vanity-black/70 text-lg leading-relaxed text-center">
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: sanitizeLimitedHtml(unescapeHtml(m.text))
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className={`grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-24 items-center`}>
                                        <div className={`space-y-8 md:col-span-8 ${m.imagePosition === 'left' ? 'md:order-2' : 'md:order-1'}`}>
                                            {m.title && <h2 className="font-serif text-4xl md:text-5xl leading-tight">{m.title}</h2>}
                                            {m.text && (
                                                <div className="vanity-content font-sans text-vanity-black/70 text-lg leading-relaxed text-center">
                                                    <div
                                                        dangerouslySetInnerHTML={{
                                                            __html: sanitizeLimitedHtml(unescapeHtml(m.text))
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        <div className={`relative aspect-[4/5] overflow-hidden rounded-3xl shadow-2xl md:col-span-4 ${m.imagePosition === 'left' ? 'md:order-1' : 'md:order-2'}`}>
                                            {m.imageUrl ? (
                                                <img src={m.imageUrl} alt={m.altText || m.title || page.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
                                            ) : (
                                                <div className="w-full h-full bg-gray-50 flex items-center justify-center font-serif italic text-gray-300 text-2xl">Visual Unavailable</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </section>
                        ))}
                    </div>
                )}

                {/* System Content (Templates) */}
                {page.slug === 'contact' && <TemplateContact page={page} />}
                {page.slug === 'archive' && <TemplateArchive />}
            </div>
        </div>
    );
};

export default DynamicPage;
